import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import MDTypography from "components/MDTypography";
import { bookDemo } from "./api";
import { publicLinks } from "./publicLinks";
import { toast } from "react-toastify";
import MDButton from "components/MDButton";

const DemoForm = ({ open, onClose }) => {
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      employeeSize: "",
      consent: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone number is required"),
      employeeSize: Yup.string().required("Please select an option"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        // Ensure employeeSize is sent as a string (not as an object or empty)
        const payload = {
          ...values,
          employeeSize: String(values.employeeSize || ""),
        };
        const response = await bookDemo(payload);
        if (response.success) {
          toast.success("Demo request sent successfully!");
        } else {
          toast.error(response.error || "Failed to send demo request.");
        }
        onClose();
        resetForm();
      } catch (error) {
        toast.error("Failed to send demo request. Please try again.");
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md"
      PaperProps={{ sx: { m: { xs: 2, sm: 4 }, width: { xs: "calc(100% - 32px)", sm: "calc(100% - 64px)" }, maxHeight: "calc(100dvh - 32px)" } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          p: 2,
          gap: 2,
          alignItems: "stretch",
          bgcolor: "#f9f9f9",
          position: "relative",
        }}
      >
        {/* Close button for mobile view */}
        <IconButton
          aria-label="Close demo form"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: { xs: 8, md: 16 },
            right: { xs: 8, md: 16 },
            zIndex: 3,
            width: 44,
            height: 44,
            
         
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            textAlign: "center",
            mb: { xs: 2, md: 0 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: "1.5rem", md: "2rem" } }}>
           Simplify Your <span style={{ color: "#1a73e8" }}>Sales Workflow</span>
          </Typography>
          <Typography sx={{ mt: 2 }}>
           Book a demo to see how SalesHive CRM simplifies your sales and customer management.
          </Typography>
          <Typography sx={{ mt: 2, fontSize: "0.9rem" }}>
            Prefer to connect via email? Reach us at <br />
            <a href="mailto:crm@saleshive.cloud" style={{ color: "#1a73e8" }}>
              crm@saleshive.cloud
            </a>
          </Typography>
        </Box>

        <Box sx={{ flex: 1, position: "relative" }}>
          {/* Close button for desktop view */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 2,
              display: { xs: "none", md: "block" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: { xs: 2, md: 4 },
              px: { xs: 0.5, md: 2 },
            }}
          >
            <TextField
              label="Name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              fullWidth
              required
              variant="outlined"
            />
            <TextField
              label="Business Email"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
              required
              variant="outlined"
            />
            <Box>
              <PhoneInput
                country={"in"}
                value={formik.values.phone}
                onChange={(phone) => formik.setFieldValue("phone", phone)}
                onBlur={() => formik.setFieldTouched("phone", true)}
                inputStyle={{
                  width: "100%",
                  height: "56px",
                  borderRadius: "4px",
                  borderColor:
                    formik.touched.phone && formik.errors.phone
                      ? "red"
                      : "#c4c4c4",
                }}
              />
              {formik.touched.phone && formik.errors.phone && (
                <Typography variant="caption" color="error">
                  {formik.errors.phone}
                </Typography>
              )}
            </Box>
            <TextField
              label="Company Name"
              name="company"
              value={formik.values.company}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="No. of Employees"
              name="employeeSize"
              value={formik.values.employeeSize}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              select
              fullWidth
              variant="outlined"
              error={
                formik.touched.employeeSize &&
                Boolean(formik.errors.employeeSize)
              }
              helperText={
                formik.touched.employeeSize && formik.errors.employeeSize
              }
              size="medium"
              InputProps={{
                sx: {
                  height: 43,
                  borderRadius: 2,
                },
              }}
              InputLabelProps={{ style: { fontWeight: 500 } }}
            >
              {["1-10", "11-50", "51-200", "201-500", "500+"].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                m: 0,
                gap: 1,
              }}
              control={
                <Checkbox
                  checked={formik.values.consent}
                  onChange={formik.handleChange}
                  name="consent"
                  size="small"
                  sx={{ p: 0.5, mt: 0.2 }}
                />
              }
              label={
                <span
                  style={{
                    display: "inline",
                    fontSize: "0.85rem",
                    fontWeight: 400,
                  }}
                >
                  Yes, keep me posted on the latest news from SalesHive.&nbsp;
                  <a
                    href={publicLinks.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1a73e8",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    Privacy Policy
                  </a>
                </span>
              }
            />
            <MDButton
              onClick={formik.handleSubmit}
              variant="gradient"
              color="success"
              
              fullWidth
            >
              Submit
            </MDButton>
          </DialogContent>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DemoForm;
